import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SocialSidebar from '../components/layout/SocialSidebar';
import LoanApplicationModal from '../components/loans/LoanApplicationModal';
import { leasingLoansProducts, companyInfo } from '../data/mockData';
import {
  Car, Wallet, Building2, Home, GraduationCap, FileText,
  Phone, Mail, Download, CheckCircle, Calculator, ArrowRight,
  Users, Clock, Shield, TrendingUp
} from 'lucide-react';

const iconMap = {
  Car, Wallet, Building2, Home, GraduationCap, FileText
};

// Loan EMI Calculator Component - Works for Auto Loan, Personal Loan, Business Loan
const LoanCalculator = ({ product }) => {
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [interestRate, setInterestRate] = useState(15);
  const [rateType, setRateType] = useState('yearly'); // 'monthly' or 'yearly'
  const [tenure, setTenure] = useState(36);
  const [showSchedule, setShowSchedule] = useState(false);

  const calculations = useMemo(() => {
    const principal = loanAmount;
    // If monthly rate selected, use directly. If yearly, divide by 12.
    const monthlyRate = rateType === 'monthly'
      ? interestRate / 100
      : interestRate / 12 / 100;
    const n = tenure;

    // Monthly Interest for simple display (first month interest)
    const monthlyInterest = principal * monthlyRate;

    // EMI Calculation using standard formula
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) /
      (Math.pow(1 + monthlyRate, n) - 1);

    const totalPayment = emi * n;
    const totalInterest = totalPayment - principal;

    // Amortization schedule
    const schedule = [];
    let balance = principal;
    for (let i = 1; i <= n; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;
      schedule.push({
        month: i,
        emi: emi,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }

    return {
      emi: isNaN(emi) ? 0 : emi,
      totalPayment: isNaN(totalPayment) ? 0 : totalPayment,
      totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
      monthlyInterest: isNaN(monthlyInterest) ? 0 : monthlyInterest,
      monthlyRate: monthlyRate * 100,
      schedule
    };
  }, [loanAmount, interestRate, rateType, tenure]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const principalPercentage = (loanAmount / calculations.totalPayment) * 100;
  const interestPercentage = (calculations.totalInterest / calculations.totalPayment) * 100;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-[#2A9D8F] to-[#004488] p-6">
        <div className="flex items-center text-white">
          <Calculator size={28} className="mr-3" />
          <div>
            <h3 className="text-xl font-bold">{product.title} EMI Calculator</h3>
            <p className="text-white/80 text-sm">Calculate your monthly installments instantly</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            {/* Loan Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loan Amount: <span className="text-[#2A9D8F] font-bold">{formatCurrency(loanAmount)}</span>
              </label>
              <input
                type="range"
                min={product?.loanAmounts?.min || 100000}
                max={product?.loanAmounts?.max || 50000000}
                step={50000}
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2A9D8F]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{formatCurrency(product?.loanAmounts?.min || 100000)}</span>
                <span>{formatCurrency(product?.loanAmounts?.max || 50000000)}</span>
              </div>
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {/* Interest Rate with Type Toggle */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Interest Rate: <span className="text-[#2A9D8F] font-bold">{interestRate}% {rateType === 'monthly' ? 'per month' : 'p.a.'}</span>
                </label>
                {/* Monthly/Yearly Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setRateType('monthly')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${rateType === 'monthly'
                      ? 'bg-[#2A9D8F] text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setRateType('yearly')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${rateType === 'yearly'
                      ? 'bg-[#2A9D8F] text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Yearly
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={rateType === 'monthly' ? 0.5 : 5}
                max={rateType === 'monthly' ? 5 : 35}
                step={rateType === 'monthly' ? 0.1 : 0.5}
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2A9D8F]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{rateType === 'monthly' ? '0.5%' : '5%'}</span>
                <span>{rateType === 'monthly' ? '5%' : '35%'}</span>
              </div>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                step={rateType === 'monthly' ? 0.1 : 0.5}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              {/* Show effective monthly rate when yearly is selected */}
              {rateType === 'yearly' && (
                <p className="text-xs text-gray-500 mt-1">
                  Effective monthly rate: <span className="font-medium text-[#2A9D8F]">{(interestRate / 12).toFixed(2)}%</span>
                </p>
              )}
            </div>

            {/* Tenure */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loan Tenure: <span className="text-[#2A9D8F] font-bold">{tenure} months ({(tenure / 12).toFixed(1)} years)</span>
              </label>
              <input
                type="range"
                min={product?.tenureMonths?.min || 12}
                max={product?.tenureMonths?.max || 84}
                step={6}
                value={tenure}
                onChange={(e) => setTenure(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2A9D8F]"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{product?.tenureMonths?.min || 12} months</span>
                <span>{product?.tenureMonths?.max || 84} months</span>
              </div>
              <div className="flex gap-2 mt-2">
                {[12, 24, 36, 48, 60, 72, 84].filter(t => t <= (product?.tenureMonths?.max || 84)).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTenure(t)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tenure === t
                      ? 'bg-[#2A9D8F] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    {t / 12}Y
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div>
            {/* EMI Display */}
            <div className="bg-gradient-to-br from-[#2A9D8F] to-[#004488] rounded-xl p-6 text-white mb-6">
              <p className="text-sm text-white/80 mb-1">Your Monthly EMI</p>
              <p className="text-4xl font-bold mb-4">
                {formatCurrency(calculations.emi)}
              </p>

              {/* Monthly Interest Highlight */}
              <div className="bg-white/10 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/90">Monthly Interest (1st month)</span>
                  <span className="text-xl font-bold text-yellow-300">{formatCurrency(calculations.monthlyInterest)}</span>
                </div>
                <p className="text-xs text-white/60 mt-1">
                  Based on {calculations.monthlyRate.toFixed(2)}% monthly rate on principal
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                <div>
                  <p className="text-xs text-white/70">Total Interest</p>
                  <p className="text-lg font-semibold">{formatCurrency(calculations.totalInterest)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/70">Total Payment</p>
                  <p className="text-lg font-semibold">{formatCurrency(calculations.totalPayment)}</p>
                </div>
              </div>
            </div>

            {/* Breakdown Chart */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Payment Breakdown</p>
              <div className="flex h-4 rounded-full overflow-hidden mb-3">
                <div
                  className="bg-[#2A9D8F]"
                  style={{ width: `${principalPercentage}%` }}
                  title={`Principal: ${principalPercentage.toFixed(1)}%`}
                />
                <div
                  className="bg-orange-400"
                  style={{ width: `${interestPercentage}%` }}
                  title={`Interest: ${interestPercentage.toFixed(1)}%`}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="flex items-center">
                  <span className="w-3 h-3 bg-[#2A9D8F] rounded mr-1"></span>
                  Principal ({principalPercentage.toFixed(1)}%)
                </span>
                <span className="flex items-center">
                  <span className="w-3 h-3 bg-orange-400 rounded mr-1"></span>
                  Interest ({interestPercentage.toFixed(1)}%)
                </span>
              </div>
            </div>

            {/* View Schedule Button */}
            <button
              onClick={() => setShowSchedule(!showSchedule)}
              className="w-full py-3 border-2 border-[#2A9D8F] text-[#2A9D8F] rounded-lg font-medium hover:bg-[#2A9D8F] hover:text-white transition-colors"
            >
              {showSchedule ? 'Hide' : 'View'} Amortization Schedule
            </button>
          </div>
        </div>

        {/* Amortization Schedule */}
        {showSchedule && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Amortization Schedule</h4>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left">Month</th>
                    <th className="px-4 py-3 text-right">EMI</th>
                    <th className="px-4 py-3 text-right">Principal</th>
                    <th className="px-4 py-3 text-right">Interest</th>
                    <th className="px-4 py-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.schedule.map((row) => (
                    <tr key={row.month} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2">{row.month}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.emi)}</td>
                      <td className="px-4 py-2 text-right text-[#2A9D8F]">{formatCurrency(row.principal)}</td>
                      <td className="px-4 py-2 text-right text-orange-500">{formatCurrency(row.interest)}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LeasingLoansDetailPage = () => {
  const { slug } = useParams();
  const product = leasingLoansProducts.find(p => p.slug === slug);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-[140px] pb-16">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-8">The product you are looking for does not exist.</p>
            <Link to="/products/leasing-loans" className="bg-[#2A9D8F] text-white px-6 py-3 rounded font-medium">
              View All Leasing & Loans
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const Icon = iconMap[product.icon] || Car;
  const otherProducts = leasingLoansProducts.filter(p => p.slug !== slug).slice(0, 3);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{product.title} - Leasing & Loans - {companyInfo.name}</title>
        <meta name="description" content={product.shortDescription} />
        <meta name="keywords" content={`${product.title}, ${product.slug}, loan, financing, Sri Lanka`} />
        <meta property="og:title" content={`${product.title} - ${companyInfo.name}`} />
        <meta property="og:description" content={product.shortDescription} />
        <meta property="og:image" content={product.heroImage} />
      </Helmet>

      <Header />
      <SocialSidebar />

      <main>
        {/* Hero Section - Full Height */}
        <div className="relative h-[100svh] min-h-[550px] md:min-h-[650px]">
          <img
            src={product.heroImage}
            alt={product.title}
            className="w-full h-full object-cover brightness-125 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-transparent to-black/10" />
          <div className="absolute inset-0 flex items-center pt-[100px]">
            <div className="max-w-7xl mx-auto px-4 w-full">
              <nav className="text-white/70 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/products" className="hover:text-white">Products</Link>
                <span className="mx-2">/</span>
                <Link to="/products/leasing-loans" className="hover:text-white">Leasing & Loans</Link>
                <span className="mx-2">/</span>
                <span className="text-white">{product.title}</span>
              </nav>
              <div className="flex items-center mb-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl mr-4">
                  <Icon size={36} className="text-white" />
                </div>
                <div>
                  <span className="text-white/70 text-sm">Leasing & Loans</span>
                  <h1 className="text-4xl md:text-5xl font-bold text-white">
                    {product.title}
                  </h1>
                </div>
              </div>
              <p className="text-white/90 text-lg max-w-2xl mb-6">
                {product.shortDescription}
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowApplicationModal(true)}
                  className="bg-white text-[#2A9D8F] px-6 py-3 rounded font-semibold hover:bg-gray-100 transition-colors flex items-center"
                >
                  Apply Now
                  <ArrowRight size={18} className="ml-2" />
                </button>
                {product.hasCalculator && (
                  <a
                    href="#calculator"
                    className="border-2 border-white text-white px-6 py-3 rounded font-semibold hover:bg-white hover:text-[#2A9D8F] transition-colors flex items-center"
                  >
                    <Calculator size={18} className="mr-2" />
                    EMI Calculator
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Info Bar */}
        <div className="bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between py-4 overflow-x-auto">
              <div className="flex items-center space-x-8">
                <a href="#features" className="text-gray-600 hover:text-[#2A9D8F] font-medium whitespace-nowrap flex items-center">
                  <CheckCircle size={16} className="mr-1" /> Features
                </a>
                <a href="#eligibility" className="text-gray-600 hover:text-[#2A9D8F] font-medium whitespace-nowrap flex items-center">
                  <Users size={16} className="mr-1" /> Eligibility
                </a>
                {product.hasCalculator && (
                  <a href="#calculator" className="text-gray-600 hover:text-[#2A9D8F] font-medium whitespace-nowrap flex items-center">
                    <Calculator size={16} className="mr-1" /> Calculator
                  </a>
                )}
              </div>
              <a
                href={`tel:${companyInfo.phone}`}
                className="text-[#2A9D8F] font-medium flex items-center whitespace-nowrap"
              >
                <Phone size={16} className="mr-1" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Calculator Section */}
              {product.hasCalculator && (
                <section id="calculator">
                  <LoanCalculator product={product} />
                </section>
              )}

              {/* Features Section */}
              <section id="features" className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <CheckCircle size={28} className="text-[#2A9D8F] mr-3" />
                  Key Features & Benefits
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-[#2A9D8F]/5 transition-colors">
                      <div className="w-6 h-6 bg-[#2A9D8F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Eligibility Section */}
              <section id="eligibility" className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Users size={28} className="text-[#2A9D8F] mr-3" />
                  Eligibility Criteria
                </h2>
                <ul className="space-y-3">
                  {product.eligibility.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3 p-3 border-l-4 border-[#2A9D8F] bg-gray-50">
                      <ArrowRight size={18} className="text-[#2A9D8F] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Documents Section */}
              <section id="documents" className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FileText size={28} className="text-[#2A9D8F] mr-3" />
                  Required Documents
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.documents.map((doc, index) => (
                    <div key={index} className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-[#2A9D8F] transition-colors">
                      <FileText size={20} className="text-[#2A9D8F]" />
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Apply Card */}
              <div className="bg-gradient-to-br from-[#2A9D8F] to-[#004488] text-white rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">Ready to Apply?</h3>
                <p className="text-white/80 mb-6">
                  Get your {product.title} approved within 24 hours. Apply now!
                </p>
                <button
                  onClick={() => setShowApplicationModal(true)}
                  className="block w-full bg-white text-[#2A9D8F] text-center py-3 rounded font-semibold hover:bg-gray-100 transition-colors mb-3"
                >
                  Apply Now
                </button>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="block w-full border-2 border-white text-white text-center py-3 rounded font-semibold hover:bg-white hover:text-[#2A9D8F] transition-colors"
                >
                  <Phone size={16} className="inline mr-2" />
                  Call Us
                </a>
              </div>

              {/* Contact Card */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
                <div className="space-y-4">
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center space-x-3 text-gray-600 hover:text-[#2A9D8F]"
                  >
                    <div className="p-2 bg-[#2A9D8F]/10 rounded-full">
                      <Phone size={18} className="text-[#2A9D8F]" />
                    </div>
                    <span>{companyInfo.phone}</span>
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center space-x-3 text-gray-600 hover:text-[#2A9D8F]"
                  >
                    <div className="p-2 bg-[#2A9D8F]/10 rounded-full">
                      <Mail size={18} className="text-[#2A9D8F]" />
                    </div>
                    <span>{companyInfo.email}</span>
                  </a>
                </div>
              </div>

              {/* Quick Facts */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Facts</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600 flex items-center">
                      <Clock size={16} className="mr-2 text-[#2A9D8F]" />
                      Processing Time
                    </span>
                    <span className="font-semibold text-gray-900">24-48 hrs</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600 flex items-center">
                      <TrendingUp size={16} className="mr-2 text-[#2A9D8F]" />
                      Starting Rate
                    </span>
                    <span className="font-semibold text-[#2A9D8F]">{product.interestRates[0]?.rate}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600 flex items-center">
                      <Shield size={16} className="mr-2 text-[#2A9D8F]" />
                      Insurance
                    </span>
                    <span className="font-semibold text-gray-900">Available</span>
                  </div>
                </div>
              </div>

              {/* Download */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Downloads</h3>
                <button className="w-full flex items-center justify-center space-x-2 border-2 border-[#2A9D8F] text-[#2A9D8F] py-3 rounded-lg font-medium hover:bg-[#2A9D8F] hover:text-white transition-colors">
                  <Download size={18} />
                  <span>Application Form</span>
                </button>
              </div>
            </div>
          </div>

          {/* Other Products */}
          {otherProducts.length > 0 && (
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Other Leasing & Loan Products</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {otherProducts.map((otherProduct) => {
                  const OtherIcon = iconMap[otherProduct.icon] || Car;
                  return (
                    <Link
                      key={otherProduct.slug}
                      to={`/products/leasing-loans/${otherProduct.slug}`}
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow group"
                    >
                      <div className="h-40 overflow-hidden relative">
                        <img
                          src={otherProduct.image}
                          alt={otherProduct.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-3 left-3 flex items-center text-white">
                          <OtherIcon size={20} className="mr-2" />
                          <span className="font-semibold">{otherProduct.title}</span>
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="text-sm text-gray-500 line-clamp-2">{otherProduct.shortDescription}</p>
                        <span className="text-[#2A9D8F] text-sm font-medium mt-2 inline-flex items-center">
                          Learn More <ArrowRight size={14} className="ml-1" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Loan Application Modal */}
      <LoanApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        product={product}
      />

      <Footer />
    </div>
  );
};

export default LeasingLoansDetailPage;
